import { useMemo, useState } from "react";
import { categories, topics, type Topic } from "@/mocks/observeData";
import { CategoryTag, SectionHeading, SectionShell, StatusBadge } from "./common";

function ConcernBars({ value }: { value: number }) {
  return (
    <span className="flex items-center gap-1" aria-label={`热度 ${value}/5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`h-1.5 w-4 rounded-sm ${n <= value ? "bg-primary-500" : "bg-secondary-200"}`}
        />
      ))}
    </span>
  );
}

function TopicCard({ t, index }: { t: Topic; index: number }) {
  return (
    <article className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-heading text-2xl font-black text-secondary-300 group-hover:text-primary-500 transition-colors">
            {String(index + 1).padStart(2, "0")}
          </span>
          <StatusBadge status={t.status} />
        </div>
        <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
          {t.category === "food"
            ? "食品餐饮"
            : t.category === "digital"
              ? "3C数码"
              : t.category === "beauty"
                ? "美妆个护"
                : t.category === "retail"
                  ? "零售消费"
                  : t.category === "finance"
                    ? "金融保险"
                    : t.category === "auto"
                      ? "汽车出行"
                      : t.category === "life"
                        ? "本地生活"
                        : "互联网"}
        </span>
      </div>

      <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
        {t.title}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600 line-clamp-3">{t.summary}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {t.tags.map((tag) => (
          <span key={tag} className="whitespace-nowrap rounded-md bg-background-100 px-2 py-1 text-xs text-foreground-600">
            #{tag}
          </span>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
        <ConcernBars value={t.concern} />
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-time-line" />更新 {t.updated}
        </span>
      </div>
    </article>
  );
}

export default function TopicSection() {
  const [active, setActive] = useState("all");
  const list = useMemo(
    () => (active === "all" ? topics : topics.filter((t) => t.category === active)),
    [active],
  );

  return (
    <SectionShell id="topic" className="bg-background-50 py-14 md:py-20">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading
          eyebrow="HOT TOPICS"
          title="热点议题追踪"
          desc="按关注热度整理当下读者最关心的企业与品牌议题。每条均标注核实状态，可点击行业标签筛选聚焦。"
        />
        <p className="shrink-0 text-sm text-foreground-500">
          共 {list.length} 个议题 · 点击标签筛选行业
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {categories.map((c) => (
          <CategoryTag
            key={c.id}
            label={c.name}
            active={active === c.id}
            onClick={() => setActive(c.id)}
          />
        ))}
      </div>

      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((t, i) => (
          <TopicCard key={t.id} t={t} index={i} />
        ))}
      </div>
    </SectionShell>
  );
}